import React, { useEffect, useState } from "react";
import {
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
} from "@mui/material";
import { useRegion } from "../../context/RegionContext";
import { REGIONS } from "../../constants/regions";
import { getCountries, loadCountries } from "../../utils/countryCache";
import { CountryItem } from "../../models/master";
import "./CountrySelector.css";

const DropdownIcon = () => (
  <svg
    width="14"
    height="9"
    viewBox="0 0 14 9"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="country-selector-icon"
  >
    <path
      d="M12.8332 1.5L6.99984 7.33333L1.1665 1.5"
      stroke="#5C5F62"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface CountrySelectorProps {
  mobile?: boolean;
}

const CountrySelector: React.FC<CountrySelectorProps> = ({
  mobile = false,
}) => {
  const { region, countryId: storedCountryId, changeRegion, setCountryId } = useRegion();
  const [countries, setCountries] = useState<CountryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        await loadCountries();
        const cachedCountries = getCountries();
        if (cachedCountries) {
          setCountries(cachedCountries);
        }
      } catch (error) {
        console.error("Error loading countries:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, []);

  // When countries load and we have a current country from region but no stored countryId, persist it
  // so that on next page load localStorage has both region and countryId (from API response id).
  const currentCountryFromRegion = countries.length > 0 && !storedCountryId ? (() => {
    const normalizedRegion = region.toLowerCase();
    return countries.find((country) => {
      const countryShort = country.countryShort?.toLowerCase();
      const countryCode = country.countryCode?.toLowerCase();
      return (
        (normalizedRegion === REGIONS.AE && (countryShort === "uae" || countryShort === "ae" || countryCode === "ae")) ||
        (normalizedRegion === REGIONS.SA && (countryShort === "ksa" || countryShort === "sa" || countryCode === "sa"))
      );
    });
  })() : null;

  useEffect(() => {
    if (currentCountryFromRegion?.id && !storedCountryId) {
      setCountryId(currentCountryFromRegion.id);
    }
  }, [currentCountryFromRegion?.id, storedCountryId, setCountryId]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const selectedCountryId = event.target.value;
    const selectedCountry = countries.find((c) => c.id === selectedCountryId);
    
    if (selectedCountry) {
      const countryShort = selectedCountry.countryShort?.toLowerCase();
      const countryCode = selectedCountry.countryCode?.toLowerCase();
      let newRegion: string = REGIONS.SA;
      if (countryShort === "uae" || countryShort === "ae" || countryCode === "ae") {
        newRegion = REGIONS.AE;
      } else if (countryShort === "ksa" || countryShort === "sa" || countryCode === "sa") {
        newRegion = REGIONS.SA;
      }
      changeRegion(newRegion as (typeof REGIONS)[keyof typeof REGIONS], selectedCountry.id);
    }
  };

  // Find current country: prefer stored countryId, else match by region
  const getCurrentCountry = (): CountryItem | undefined => {
    if (storedCountryId && countries.length > 0) {
      const byId = countries.find((c) => c.id === storedCountryId);
      if (byId) return byId;
    }
    const normalizedRegion = region.toLowerCase();
    return countries.find((country) => {
      const countryShort = country.countryShort?.toLowerCase();
      const countryCode = country.countryCode?.toLowerCase();
      return (
        (normalizedRegion === REGIONS.AE && (countryShort === "uae" || countryShort === "ae" || countryCode === "ae")) ||
        (normalizedRegion === REGIONS.SA && (countryShort === "ksa" || countryShort === "sa" || countryCode === "sa"))
      );
    });
  };

  const currentCountry = getCurrentCountry();

  const getCountryCode = () => {
    if (currentCountry) {
      return currentCountry.countryShort || currentCountry.countryCode || "KSA";
    }
    return region === REGIONS.AE ? "UAE" : "KSA";
  };

  const renderFlag = () => {
    if (currentCountry?.flagUrl) {
      // Check if flagUrl is an image URL (starts with http) or an emoji
      if (currentCountry.flagUrl.startsWith("http")) {
        return (
          <img
            src={currentCountry.flagUrl}
            alt={currentCountry.countryName}
            style={{ width: "20px", height: "15px", objectFit: "cover" }}
          />
        );
      }
      // It's an emoji
      return currentCountry.flagUrl;
    }
    // Fallback to emoji based on region
    return region === REGIONS.AE ? "🇦🇪" : "🇸🇦";
  };

  const renderValue = () => {
    const flag = renderFlag();
    
    if (mobile) {
      // Mobile version: just show country code and flag
      return (
        <div className="country-selector-display-mobile">
          <span className="country-code-mobile">{getCountryCode()}</span>
          <span className="country-flag-mobile">{flag}</span>
        </div>
      );
    }
    // Desktop version: show flag, label, and code
    return (
      <div className="country-selector-display">
        <span className="country-flag">{flag}</span>
        <div className="country-text">
          <span className="country-label">Country</span>
          <span className="country-code">{getCountryCode()}</span>
        </div>
      </div>
    );
  };

  return (
    <FormControl
      className={`country-selector-wrapper ${mobile ? "mobile" : ""}`}
    >
      <Select
        value={currentCountry?.id || ""}
        onChange={handleChange}
        renderValue={renderValue}
        className="country-selector"
        IconComponent={DropdownIcon}
        disabled={loading || countries.length === 0}
        sx={{
          "& .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            border: "none",
          },
          "& .MuiSelect-icon": {
            right: "4px",
            pointerEvents: "none",
          },
          ...(mobile && {
            "& .MuiSelect-select": {
              padding: "4px 20px 4px 0 !important",
            },
          }),
        }}
      >
        {countries.map((country) => (
          <MenuItem key={country.id} value={country.id}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {country.flagUrl ? (
                <img
                  src={country.flagUrl}
                  alt={country.countryName}
                  style={{ width: "20px", height: "15px", objectFit: "cover" }}
                />
              ) : (
                <span>
                  {country.countryCode === "AE" ? "🇦🇪" : country.countryCode === "SA" ? "🇸🇦" : "🌐"}
                </span>
              )}
              <span>{country.countryShort || country.countryName}</span>
            </div>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default CountrySelector;
